import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';

import { createRef } from 'react';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  Question,
  SelectLang,
  ModuleTabs,
  ChunkErrorBoundary,
  NotifyBell,
} from '@/components';
import { currentUser as queryCurrentUser } from '@/services/api';
import { clearAuthData } from '@/utils/auth';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { convertMenusToRoutes, filterMenusByModule } from '@/utils/menuUtils';
import '@ant-design/v5-patch-for-react-19';
import React from 'react';

// 加载iconfont Symbol脚本
const loadIconFontScript = () => {
  // 检查是否已经加载过
  if (document.querySelector('script[src="/iconfont/iconfont.js"]')) {
    return;
  }
  
  const script = document.createElement('script');
  script.src = '/iconfont/iconfont.js';
  script.onload = () => {
    console.log('Iconfont Symbol script loaded successfully');
  };
  script.onerror = () => {
    console.error('Failed to load iconfont Symbol script');
  };
  document.head.appendChild(script);
};

// 在应用启动时加载脚本
loadIconFontScript();

const isDev = process.env.NODE_ENV === 'development';
const isDevOrTest = isDev || process.env.CI;
const loginPath = '/login';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  currentModule?: string;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      // 确保返回的数据包含权限信息
      return {
        ...msg.data.user,
        name: msg.data.user.nickname,
        workspaceMenus: msg.data.workspaceMenus || [],
        manageMenus: msg.data.manageMenus || [],
        permissions: msg.data.permissions || [],
        roles: msg.data.roles || []
      };
    } catch (_error) {
      // 获取用户信息失败时，清除认证数据并跳转到登录页面
      clearAuthData();
      history.push(loginPath);
    }
    return undefined;
  };
  
  // 如果是登录页面，不执行用户信息获取
  const { location } = history;
  if (
    ![loginPath, '/user/register', '/user/register-result'].includes(
      location.pathname,
    )
  ) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      currentModule: 'workspace',
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  
  // 登录页面时，只返回基本配置，不获取用户信息
  return {
    fetchUserInfo,
    currentModule: 'workspace',
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  // 根据当前模块过滤菜单
  const getFilteredRoutes = (activeKey?: string) => {
    const { location } = history;
    const currentPath = location.pathname;
    
    let menus: any[] = [];
    if(!activeKey){
      activeKey = initialState?.currentModule;
    }
    // 优先使用currentModule状态，如果没有则根据路径判断
    const moduleType = activeKey || 
      (currentPath.startsWith('/manage') ? 'manage' : 
       currentPath.startsWith('/workspace') ? 'workspace' : 'workspace');
    
    if(moduleType == 'workspace'){
      menus = initialState?.currentUser?.workspaceMenus || [];
    } else if(moduleType == 'manage'){
      menus = initialState?.currentUser?.manageMenus || [];
    }
    
    console.log('Selected menus for module:', moduleType, menus);
    
    // 直接转换菜单，不需要再次过滤
    const routes = convertMenusToRoutes(menus);
    console.log('Converted routes:', routes);
    return routes;
  };

  return {
    actionsRender: () => [
      <NotifyBell key="NotifyBell" />,
      <Question key="doc" />,
      <SelectLang key="SelectLang" />,
    ],
    headerContentRender: () => {
      const workspaceMenus = initialState?.currentUser?.workspaceMenus || []
      const manageMenus = initialState?.currentUser?.manageMenus || []
      const hasWorkspace = workspaceMenus.length > 0
      const hasManage = manageMenus.length > 0
      
      // 如果两个菜单都为空，则不显示 ModuleTabs
      if (!hasWorkspace && !hasManage) {
        return null
      }
      
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          width: '100%'
        }}>
          <div style={{ 
            flexShrink: 0,
            minWidth: 'fit-content'
          }}>
            <ModuleTabs
              workspaceMenus={workspaceMenus}
              manageMenus={manageMenus}
              onTabChange={(activeKey) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  currentModule: activeKey,
                }));
                getFilteredRoutes(activeKey);
              }}
            />
          </div>
        </div>
      )
    },
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => (
        <AvatarDropdown>{avatarChildren}</AvatarDropdown>
      ),
    },
    // waterMarkProps: {
    //   content: initialState?.currentUser?.name,
    // },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果是登录页面，不需要检查用户状态
      if (location.pathname === loginPath) {
        return;
      }
      
      // 如果没有登录用户，重定向到登录页面
      if (!initialState?.currentUser) {
        // 清除可能残留的认证数据
        clearAuthData();
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDevOrTest
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>帮助文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    menu: {
      // 添加params来强制重新渲染菜单
      params: {
        module: initialState?.currentModule || 'workspace',
      },
      request: async (params, defaultMenuData) => {
        console.log('Menu request');
        const routes = getFilteredRoutes();
        console.log('Menu request returning routes:', routes);
        return routes;
      },
      key: `${initialState?.currentModule || 'workspace'}-${Date.now()}`,
    },
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          <ChunkErrorBoundary>
            {children}
          </ChunkErrorBoundary>
          {isDevOrTest && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

// const authHeaderInterceptor = (url: string, options: RequestConfig) => {
//   const authHeader = { 'Tenant-Id': '1'};//'Access-Token': getAccessToken(), 
//   return {
//     url: `${url}`,
//     options: { ...options, interceptors: true, headers: authHeader },
//   };
// };
/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  ...errorConfig,
};
